import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ChevronsUpDown } from "lucide-react"
import * as React from "react"
import { useSupplierList } from "../../../api/suppliersApi"

interface SupplierSearchPickerProps {
  /** Label shown on the trigger — the currently chosen supplier, or a prompt. */
  triggerLabel: string
  onSelect: (supplierId: string, label: string) => void
  className?: string
}

/**
 * Searchable single-select supplier combobox, filtered by name/number
 * server-side — modeled on SubstancePicker.tsx. No "load more": same
 * reasoning as SubstancePicker, the server-side search already narrows the
 * 50-row page enough that a second page is rarely needed.
 */
export function SupplierSearchPicker({
  triggerLabel,
  onSelect,
  className,
}: SupplierSearchPickerProps) {
  const { translate } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [q, setQ] = React.useState("")
  const debouncedQ = useDebouncedValue(q, 250)

  const {
    data: supplierPages,
    isFetching,
    isPlaceholderData,
  } = useSupplierList({
    q: debouncedQ || undefined,
    limit: 50,
  })

  // Results are stale — either still the previous search's rows
  // (`keepPreviousData` placeholder) or not yet reflecting the latest
  // keystroke (debounce window hasn't elapsed) — so they must not be
  // selectable: a stale row rendered under a since-changed query would let
  // Enter/click assign the wrong supplier.
  const isStale = isPlaceholderData || q !== debouncedQ

  const suppliers = React.useMemo(
    () => supplierPages?.pages.flatMap((p) => p.items) ?? [],
    [supplierPages],
  )

  const handleSelect = React.useCallback(
    (supplierId: string) => {
      if (isStale) return
      const supplier = suppliers.find((s) => s.id === supplierId)
      if (!supplier) return
      onSelect(supplier.id, supplier.name)
      setOpen(false)
      setQ("")
    },
    [suppliers, onSelect, isStale],
  )

  const handleOpenChange = React.useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) setQ("")
  }, [])

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex h-9 w-full items-center justify-between gap-1 rounded-md border border-grey4 bg-grey2 px-3 text-[13px] text-white hover:bg-primary/4",
            className,
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown size={13} className="shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={translate("MC_MD_LINK_SUPPLIER_SEARCH")}
            value={q}
            onValueChange={setQ}
          />
          <CommandList className="max-h-[260px]">
            {!isFetching && suppliers.length === 0 && (
              <CommandEmpty>
                {translate("MC_MD_LINK_SUPPLIER_SEARCH_NONE")}
              </CommandEmpty>
            )}
            {suppliers.length > 0 && (
              <CommandGroup className="overflow-y-auto">
                {suppliers.map((supplier) => (
                  <CommandItem
                    key={supplier.id}
                    value={supplier.id}
                    onSelect={handleSelect}
                    className="text-[13px]"
                  >
                    <span className="font-mono">
                      {supplier.supplierNumber ?? "—"}
                    </span>
                    <span className="ml-1.5 truncate text-grey5">
                      {supplier.name}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
